import React from 'react'
import { Accordion, AccordionDetails, AccordionSummary, Grid, TextField, Typography, Box, InputAdornment, Divider } from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import classNames from 'classnames'
import { useStyles } from './style'
import { removeLeadingZeros } from '../../../../util/form-utils'

const Details = ({ assessment, setAssessment }) => {
  const classes = useStyles()
  const [expanded, setExpanded] = React.useState('basic-info')

  const handleExpand = (panel) => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : false)
  }

  const handleTitleChange = (event) => {
    const { name, value } = event.target
    setAssessment((prevData) => ({
      ...prevData,
      [name]: value
    }))
  }

  const handleAssessmentSectionIdChange = (metaSkillIndex, event) => {
    event.target.value = removeLeadingZeros(event.target.value)
    const newSectionId = {
      assessment: Number(event.target.value)
    }
    const updatedMetaSkill = [...assessment.metaSkills]
    updatedMetaSkill[metaSkillIndex] = {
      ...updatedMetaSkill[metaSkillIndex],
      sectionId: { ...updatedMetaSkill[metaSkillIndex].sectionId, ...newSectionId }
    }
    setAssessment((prevData) => ({
      ...prevData,
      metaSkills: updatedMetaSkill
    }))
  }

  const handleTrialSectionIdChange = (metaSkillIndex, event) => {
    event.target.value = removeLeadingZeros(event.target.value)
    const newSectionId = {
      trial: Number(event.target.value)
    }
    const updatedMetaSkill = [...assessment.metaSkills]
    updatedMetaSkill[metaSkillIndex] = {
      ...updatedMetaSkill[metaSkillIndex],
      sectionId: { ...updatedMetaSkill[metaSkillIndex].sectionId, ...newSectionId }
    }
    setAssessment((prevData) => ({
      ...prevData,
      metaSkills: updatedMetaSkill
    }))
  }

  const handleDescriptionChange = (metaSkillIndex, event) => {
    const newDescription = event.target.value
    const updatedMetaSkill = [...assessment.metaSkills]
    updatedMetaSkill[metaSkillIndex] = { ...updatedMetaSkill[metaSkillIndex], description: newDescription }
    setAssessment((prevData) => ({
      ...prevData,
      metaSkills: updatedMetaSkill
    }))
  }

  return (
    <div
      className={classNames(classes.root, {
        [classes.rootCollapsed]: expanded === 'basic-info'
      })}
    >
      <Accordion
        elevation={0}
        classes={{
          root: classes.accordion,
          expanded: classes.accordionExapanded
        }}
        expanded={expanded === 'basic-info'}
        onChange={handleExpand('basic-info')}
      >
        <AccordionSummary
          classes={{
            root: classes.summary
          }}
          expandIcon={<ExpandMoreIcon />}
          aria-controls='panel-basic'
          id='panel-basic-header'
        >
          <Typography
            className={classNames(classes.heading, {
              [classes.headingActive]: expanded === 'basic-info'
            })}
            data-test='assessment-basic-info'
          >
            Basic Information
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          classes={{
            root: classes.detail
          }}
        >
          <Box paddingTop={3}>
            <Grid container spacing={2}>
              {/* External ID Field */}
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' className={classes.subHeading}>
                  External ID
                </Typography>
                <Typography variant='body2' color='textSecondary'>
                  The External ID is a unique identifier for the Assessment on
                  Brilliant Assessments.
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  variant='outlined'
                  name='externalId'
                  label='External ID'
                  value={assessment.assessmentId}
                  disabled
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='start'>
                        <LockIcon />
                      </InputAdornment>
                    )
                  }}
                  data-test='assessment-external-id'
                />
              </Grid>
              {/* Trial External ID Field */}
              {assessment.trialAssessmentId && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Typography variant='subtitle2' className={classes.subHeading}>
                      Trial External ID
                    </Typography>
                    <Typography variant='body2' color='textSecondary'>
                      The Trial External ID is a unique identifier for the Trial Assessment on Brilliant Assessments.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      variant='outlined'
                      name='trialexternalId'
                      label='Trial External ID'
                      value={assessment.trialAssessmentId}
                      disabled
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position='start'>
                            <LockIcon />
                          </InputAdornment>
                        )
                      }}
                      data-test='assessment-trial-external-id'
                    />
                  </Grid>
                </>
              )}

              {/* Title Field */}
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' className={classes.subHeading}>
                  Title
                </Typography>
                <Typography variant='body2' color='textSecondary'>
                  This is the full name of the Assessment.
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  variant='outlined'
                  name='title'
                  label='Title'
                  value={assessment.title}
                  onChange={handleTitleChange}
                  data-test='assessment-title'
                />
              </Grid>
              {/* Meta Skills fields */}
              <Grid item xs={12}>
                <Divider
                  style={{ paddingTop: '0.5rem', marginBottom: '0.5rem' }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant='subtitle1'>
                  Group Question Mapping
                </Typography>
                <Typography variant='subtitle2' className={classes.subtitleMap}>
                  Assign the starting question number for each assessment Meta
                  Skills
                </Typography>
                <Typography variant='body2' color='textSecondary'>
                  Please specify the starting question number for each session.
                  The system will automatically map the subsequent questions
                  until the next specified starting number.
                </Typography>
              </Grid>
              {assessment.metaSkills.map((metaSkill, index) => (
                <Grid item xs={12} key={index}>
                  <Typography
                    variant='subtitle2'
                    paddingY={3}
                    data-test={`assessment-metaSkill-title-${index}`}
                  >
                    {metaSkill.name}
                  </Typography>
                  <Box display='flex' alignItems='center'>
                    <Grid item xs={12} sm={8}>
                      <TextField
                        fullWidth
                        multiline
                        variant='outlined'
                        name='metaSkill-description'
                        label='Meta Skill Description'
                        value={metaSkill.description}
                        onChange={(e) => handleDescriptionChange(index, e)}
                        data-test={`assessment-metaSkill-description-${index}`}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2} />
                    <Grid item xs={12} sm={2}>
                      <Box display='flex' flexDirection='column' gap={2}>
                        <TextField
                          fullWidth
                          variant='outlined'
                          type='number'
                          name={`metaSkill[${index}].sectionId.assessment`}
                          label='Start - Assessment'
                          value={metaSkill.sectionId.assessment}
                          onChange={(e) => handleAssessmentSectionIdChange(index, e)}
                          InputProps={{
                            min: 0
                          }}
                          data-test={`assessment-metaSkill-start-${index}-assessment`}
                        />
                        <TextField
                          fullWidth
                          variant='outlined'
                          type='number'
                          name={`metaSkill[${index}].sectionId.trial`}
                          label='Start - Trial'
                          value={metaSkill.sectionId.trial}
                          onChange={(e) => handleTrialSectionIdChange(index, e)}
                          InputProps={{
                            min: 0
                          }}
                          data-test={`assessment-metaSkill-start-${index}-trial`}
                        />
                      </Box>
                    </Grid>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </AccordionDetails>
      </Accordion>
    </div>
  )
}

export default Details
